const HEX_COLOR = /^#([0-9a-f]{6})$/i;
const RGB_COLOR = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*[,)]/i;

const toHexPair = (channel: string): string => Number(channel).toString(16).padStart(2, "0");

/**
 * Normalize a CSS color to the opaque `FFRRGGBB` form exceljs requires.
 *
 * Both input shapes occur in practice: theme tokens are `#RRGGBB`, while MUI's `lighten()`/`darken()`
 * derived palette shades come back as `rgb(r, g, b)`. Anything else (CSS variables, `alpha()` output,
 * named colors) cannot be represented in a spreadsheet fill, so it throws rather than silently
 * producing an unreadable cell.
 */
export const cssColorToArgb = (color: string): string => {
	const hex = HEX_COLOR.exec(color.trim());
	if (hex) return `FF${hex[1].toUpperCase()}`;

	const rgb = RGB_COLOR.exec(color.trim());
	if (rgb) return `FF${toHexPair(rgb[1])}${toHexPair(rgb[2])}${toHexPair(rgb[3])}`.toUpperCase();

	throw new Error(`Cannot convert color "${color}" to an Excel ARGB value`);
};

/** True when `cssColorToArgb` can handle the value — lets callers pick a themed color with a safe fallback. */
export const isExcelRepresentableColor = (color: string | undefined): color is string =>
	color !== undefined && (HEX_COLOR.test(color.trim()) || RGB_COLOR.test(color.trim()));
